'use client';

/**
 * GiftsOfProductInCart
 *
 * Component to display and manage product-level gifts in the cart.
 * Converted from React Native to Next.js.
 *
 * Features:
 * 1. Display list of gifts attached to a cart item reference
 * 2. "Đổi quà" button - Opens SingleRuleGiftChangeWidget to change gift within the same rule
 * 3. Quantity change - Opens QuantityChangeGiftSelectorWidget to re-select all gifts
 * 4. Support removing individual gifts
 *
 * Gift Change Types:
 * - Type 1: Click "Đổi quà" -> Only show options from same rule, allow changing to different gift
 * - Type 2: Quantity change -> Show full gift selection with quantity stepper
 */
import { useCallback, useMemo, useRef, type FC } from 'react';
import { observer } from '@legendapp/state/react';
import type { GiftsOfProductInCartProps, GiftChangeParams } from './gifts-of-product-in-cart.types';
import { CartGiftList } from '../cart';
import type { FreeGiftsData } from '../../../../types/front.graphql';
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import type { SelectedGift } from '../../types/gift-selection.types';
import { SingleRuleGiftChangeWidget, type SingleRuleGiftChangeWidgetRef } from '../single-rule-gift-change-widget';
import {
    QuantityChangeGiftSelectorWidget,
    type QuantityChangeGiftSelectorWidgetRef,
} from '../quantity-change-gift-selector-widget';
import { cartItemReferenceStoreAPI, LogicType, ActionType } from '@kidslabs/module-quote';
import styles from './gifts-of-product-in-cart.module.css';
import { CartItemGroupVariantEnum } from '@kidslabs/mario-nextjs-react-ui-core';

export const GiftsOfProductInCart: FC<GiftsOfProductInCartProps> = observer(props => {
    const {
        disabled,
        variant = CartItemGroupVariantEnum.FULL,
        className,
        style,
        data: cartItemReference,
        onQuantityChange,
        size,
        showActionButton,
    } = props;

    // Refs for bottom sheets (matching mobile pattern)
    const singleRuleGiftChangeRef = useRef<SingleRuleGiftChangeWidgetRef>(null);
    const quantityChangeGiftSelectorRef = useRef<QuantityChangeGiftSelectorWidgetRef>(null);

    // Extract the product from the first cart item for the gift selector
    const cartProduct = useMemo(() => {
        const firstCartItem = cartItemReference?.cartItem;
        if (!firstCartItem?.product) {
            return null;
        }
        return firstCartItem.product;
    }, [cartItemReference?.cartItem]);

    // Get the quantity from cart item reference
    const productQuantity = useMemo(() => {
        return cartItemReference?.qty ?? 1;
    }, [cartItemReference?.qty]);

    // Get available gifts for the product - only call hook when product is available
    const { sortedGiftsDataList } = useProductGiftHook({
        product: cartProduct ?? undefined,
        fetchGiftsFromCache: false,
    });

    /**
     * Find the available gifts data for a specific rule
     */
    const findGiftDataForRule = useCallback(
        (ruleId: number): FreeGiftsData | null => {
            return sortedGiftsDataList.find(gd => gd.rule?.rule_id === ruleId) ?? null;
        },
        [sortedGiftsDataList]
    );

    /**
     * Find the currently selected gift for a specific rule from cart item reference
     */
    const findSelectedGiftForRule = useCallback(
        (ruleId: number): SelectedGift | null => {
            const logic = cartItemReference?.logics?.find(
                l => l.logicType === LogicType.PROMOTION && l.logicId === ruleId
            );
            if (!logic) return null;

            const giftAction = logic.actions.find(a => a.actionType === ActionType.GIFT && a.actionProduct);
            if (!giftAction?.actionProduct) return null;

            const giftData = findGiftDataForRule(ruleId);
            if (!giftData) return null;

            const actionProduct = giftAction.actionProduct as { id?: number; sku?: string };

            return {
                id: `${ruleId}-${actionProduct.id}-cart`,
                ruleId,
                productId: actionProduct.id ?? 0,
                item: giftAction.actionProduct,
                giftData,
                quantity: giftAction.actionQty ?? 1,
                giftSlots: giftAction.actionQty ?? 1,
                quantityUsedByRule: 0,
                selectionRound: 1,
                selectedAt: Date.now(),
                sessionId: logic.sessionId ?? '',
            };
        },
        [cartItemReference?.logics, findGiftDataForRule]
    );

    /**
     * Handler for "Đổi quà" button click - opens single rule gift change widget
     * Type 1: Only show options from same rule
     *
     * @param params - Centralized gift change parameters with cartItemReference and logic
     */
    const handleChangeGiftForRule = useCallback(
        (params: GiftChangeParams) => {
            const { logic } = params;
            const ruleId = logic.logicId;

            if (!ruleId) {
                console.warn('[GiftsOfProductInCart] Cannot change gift: no ruleId in logic');
                return;
            }

            if (!cartProduct) {
                console.warn('[GiftsOfProductInCart] Cannot change gift: no product available');
                return;
            }

            // Find available gifts for this rule
            const availableGiftsForRule = findGiftDataForRule(ruleId);
            if (!availableGiftsForRule) {
                console.warn('[GiftsOfProductInCart] Cannot change gift: no gifts available for rule', ruleId);
                return;
            }

            // Find currently selected gift for this rule
            const selectedGift = findSelectedGiftForRule(ruleId);
            if (!selectedGift) {
                console.warn('[GiftsOfProductInCart] Cannot change gift: no selected gift for rule', ruleId);
                return;
            }

            // Open bottom sheet with injected params including availableGiftsForRule
            singleRuleGiftChangeRef.current?.openBottomSheet({
                ...params,
                availableGiftsForRule, // Inject available gifts data
            });
        },
        [cartProduct, findGiftDataForRule, findSelectedGiftForRule]
    );

    /**
     * Handler when gift is changed within a rule
     */
    const handleGiftChanged = useCallback(
        (newGift: SelectedGift) => {
            console.log('[GiftsOfProductInCart] Gift changed:', newGift);
            // Update the cart item reference logic with new gift
            if (cartItemReference?.cartItemReferenceId) {
                const updatedLogics = cartItemReference.logics?.map(logic => {
                    if (logic.logicType === LogicType.PROMOTION && logic.logicId === newGift.ruleId) {
                        return {
                            ...logic,
                            actions: logic.actions.map(action => {
                                if (action.actionType === ActionType.GIFT) {
                                    return {
                                        ...action,
                                        // Cast gift item to be compatible with actionProduct
                                        // The actual product data comes from the gift selection which has compatible fields
                                        actionProduct: newGift.item as unknown as typeof action.actionProduct,
                                        actionQty: newGift.quantity,
                                    };
                                }
                                return action;
                            }),
                        };
                    }
                    return logic;
                });

                // Update the cart item reference in store
                cartItemReferenceStoreAPI.actions.updateItem({
                    ...cartItemReference,
                    logics: updatedLogics ?? [],
                });
            }
        },
        [cartItemReference]
    );

    /**
     * Handler when gift is removed from a rule
     */
    const handleGiftRemoved = useCallback(() => {
        console.log('[GiftsOfProductInCart] Gift removed');
        // TODO: Need to know which rule to remove from - should be passed as parameter
        // For now, this handler needs to be refactored to accept ruleId as parameter
    }, []);

    /**
     * Handler when quantity is changed
     */
    const handleQuantityChanged = useCallback(
        (newQuantity: number) => {
            onQuantityChange?.(newQuantity);
        },
        [onQuantityChange]
    );

    /**
     * Handler when gifts are confirmed after quantity change
     */
    const handleGiftsConfirmed = useCallback(() => {
        console.log('[GiftsOfProductInCart] Gifts confirmed after quantity change');
    }, []);

    /**
     * Handler when bottom sheet is closed
     */
    const handleClose = useCallback(() => {
        console.log('[GiftsOfProductInCart] Bottom sheet closed');
    }, []);

    const innerContainerClassName = useMemo(
        () => (variant === CartItemGroupVariantEnum.FULL ? undefined : styles.innerContainer),
        [variant]
    );

    const containerClassName =
        `${styles.giftListContainer} ${disabled ? styles.disabled : ''} ${className ?? ''}`.trim();
    if (!cartItemReference.logics.length) {
        return null;
    }
    return (
        <div className={containerClassName} style={style}>
            <CartGiftList
                innerContainerClassName={innerContainerClassName}
                cartItemReference={cartItemReference}
                mode="compact"
                size={size}
                onActionPress={handleChangeGiftForRule}
                showActionButton={showActionButton}
            />

            {/* Single Rule Gift Change Bottom Sheet - Always rendered so ref is available */}
            {cartProduct && (
                <SingleRuleGiftChangeWidget
                    ref={singleRuleGiftChangeRef}
                    onGiftChanged={handleGiftChanged}
                    onGiftRemoved={handleGiftRemoved}
                    onClose={handleClose}
                />
            )}

            {/* Quantity Change Gift Selector Bottom Sheet */}
            {cartProduct && (
                <QuantityChangeGiftSelectorWidget
                    ref={quantityChangeGiftSelectorRef}
                    product={cartProduct}
                    cartItemReference={cartItemReference}
                    initialQuantity={productQuantity}
                    onQuantityChange={handleQuantityChanged}
                    onConfirm={handleGiftsConfirmed}
                    onClose={handleClose}
                />
            )}
        </div>
    );
});

export default GiftsOfProductInCart;
